import { useThemeColor } from '@hooks/useThemeColor'
import { AdminPreviewBanner } from '@components/AdminPreviewBanner'
import { Footer } from '@components/Footer'
import { Header } from '@components/Header/Header'
import { BottomNavBar } from '@components/Header/BottomNavBar'
import { MobileMenu } from '@components/Header/MobileMenu'
import { HeroIcon } from '@components/HeroIcon'
import { useImageStatusStore } from '@stores/useImageStatusStore'
import { useThemeParams } from '@hooks/useThemeParams'
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Dimensions,
  ScrollView,
  TouchableOpacity,
} from 'react-native'
import { IOScrollView } from '@components/IntersectionObserver'
import { SafeAreaView } from 'react-native-safe-area-context'
import { View, YStack, isWeb, useMedia } from 'tamagui'
import { useScrollControl } from '@hooks/useScrollControl'
import { useScreenOrientation } from '@hooks/useScreenOrientation'

const HEADER_SECTION_HEIGHT = 100
const MIN_HEIGHT_DESKTOP = 50
const MIN_HEIGHT_MOBILE = 55
const SCROLL_THRESHOLD = 80
const SCROLL_TOP_BUTTON_THRESHOLD = 1200
const INITIAL_LOAD_DELAY_MS = 1000
const DATA_READY_DELAY_MS = 500
const SCROLL_EVENT_THROTTLE = 99
const INPUT_RANGE_NO_SCROLL = [-100, 620, 620] as const
const INPUT_RANGE_SCROLL = [-100, 0, 120] as const

export interface IBackRoute {
  route?: string
  label: string
  back?: () => void
  shouldDisplayLogo?: boolean
}

interface IAppLayoutProps {
  children: ReactNode
  backRoute?: IBackRoute
  hasBackButton?: boolean
  backgroundColor?: string
  hasFooter?: boolean
  hasPaddingBottom?: boolean
  hasScrollTopButton?: boolean
}

export function AppLayout({
  children,
  backRoute,
  hasBackButton = true,
  backgroundColor = '$bg-page',
  hasFooter = true,
  hasPaddingBottom = true,
  hasScrollTopButton = true,
}: IAppLayoutProps) {
  const isHighlightedImage = useImageStatusStore(
    (state) => state.isHighlightedImage,
  )
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isNavBarVisible, setIsNavBarVisible] = useState(!isWeb)
  const [showScrollTopButton, setShowScrollTopButton] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get('window').height,
  )
  const [hasScroll, setHasScroll] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const media = useMedia()
  const { data } = useThemeParams()
  const { isScrollingContent, registerScrollToTop } = useScrollControl()
  const { lockToPortrait } = useScreenOrientation()
  const scrollViewRef = useRef<ScrollView>(null)

  const contentHeightRef = useRef(contentHeight)
  contentHeightRef.current = contentHeight
  const screenHeightRef = useRef(screenHeight)
  screenHeightRef.current = screenHeight
  const isNavBarVisibleRef = useRef(isNavBarVisible)
  isNavBarVisibleRef.current = isNavBarVisible
  const showScrollTopButtonRef = useRef(showScrollTopButton)
  showScrollTopButtonRef.current = showScrollTopButton
  const scrollTopButtonAnim = useRef(new Animated.Value(0)).current

  const minHeight = media.gtMd ? MIN_HEIGHT_DESKTOP : MIN_HEIGHT_MOBILE
  const safeAreaBackground = useThemeColor('bg-page')
  const scrollOffsetY = useRef(new Animated.Value(0)).current

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false)
    }, INITIAL_LOAD_DELAY_MS)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (isInitialLoad) return

    const hasScrollableContent = contentHeight > screenHeight
    setHasScroll(hasScrollableContent)

    if (!isWeb) {
      setIsNavBarVisible(true)
      return
    }

    if (!data) {
      setIsNavBarVisible(false)
      return
    }

    const timer = setTimeout(() => {
      const recalculated = contentHeight > screenHeight
      setHasScroll(recalculated)
      setIsNavBarVisible(recalculated)
    }, DATA_READY_DELAY_MS)

    return () => clearTimeout(timer)
  }, [contentHeight, screenHeight, isInitialLoad, data])

  const inputRange = useMemo(
    () => (hasScroll ? [...INPUT_RANGE_SCROLL] : [...INPUT_RANGE_NO_SCROLL]),
    [hasScroll],
  )

  const animatedHeaderHeight = useMemo(
    () =>
      scrollOffsetY.interpolate({
        inputRange,
        outputRange: [
          HEADER_SECTION_HEIGHT,
          HEADER_SECTION_HEIGHT - minHeight,
          HEADER_SECTION_HEIGHT - minHeight,
        ],
        extrapolate: 'clamp',
      }),
    [inputRange, scrollOffsetY, minHeight],
  )

  const animatedOpacity = useMemo(
    () =>
      scrollOffsetY.interpolate({
        inputRange: hasScroll ? [0, 120] : [0, 620],
        outputRange: hasScroll ? [0, 1] : [1, 1],
        extrapolate: 'clamp',
      }),
    [hasScroll, scrollOffsetY],
  )

  const animatedBottom = useMemo(
    () =>
      scrollOffsetY.interpolate({
        inputRange: hasScroll ? [-20, 0, 150] : [-20, 0, 620],
        outputRange: isWeb ? [0, 30, 70] : [0, 0, 70],
        extrapolate: 'clamp',
      }),
    [hasScroll, scrollOffsetY],
  )

  const navigationBottom = useMemo(() => {
    if (media.gtMd) return 'auto'
    if (isWeb) return animatedBottom
    return 0
  }, [media.gtMd, animatedBottom])

  useEffect(() => {
    if (!media.gtSm) return

    const listenerId = animatedOpacity.addListener(({ value }) => {
      const shouldBeVisible = !!Math.floor(value)
      setIsVisible((prev) => {
        if (prev !== shouldBeVisible) return shouldBeVisible
        return prev
      })
    })

    return () => {
      animatedOpacity.removeListener(listenerId)
    }
  }, [media.gtSm, animatedOpacity])

  const handleScrollToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true })
  }, [])

  useEffect(() => {
    Animated.timing(scrollTopButtonAnim, {
      toValue: showScrollTopButton ? 1 : 0,
      duration: 220,
      useNativeDriver: true,
    }).start()
  }, [showScrollTopButton, scrollTopButtonAnim])

  const scrollTopButtonTranslateY = scrollTopButtonAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [64, 0],
  })

  useEffect(() => {
    registerScrollToTop(handleScrollToTop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleScrollToTop])

  useEffect(() => {
    if (isWeb) return
    lockToPortrait()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headerBackRoute = hasBackButton ? backRoute : undefined
  const logoUrl = data?.school?.logo

  const handleOpenMenu = useCallback(() => setIsMenuOpen(true), [])
  const handleCloseMenu = useCallback(() => setIsMenuOpen(false), [])

  const handleLayout = useCallback(
    (event: { nativeEvent: { layout: { height: number } } }) => {
      setScreenHeight(event.nativeEvent.layout.height)
    },
    [],
  )

  const handleContentSizeChange = useCallback((_: number, height: number) => {
    setContentHeight(height)
  }, [])

  const scrollListener = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (contentHeightRef.current > screenHeightRef.current) {
        const offsetY = event.nativeEvent.contentOffset.y
        if (isWeb) {
          if (offsetY > SCROLL_THRESHOLD && !isNavBarVisibleRef.current) {
            setIsNavBarVisible(true)
          } else if (offsetY < SCROLL_THRESHOLD && isNavBarVisibleRef.current) {
            setIsNavBarVisible(false)
          }
        }

        const shouldShowScrollTop = offsetY > SCROLL_TOP_BUTTON_THRESHOLD
        if (shouldShowScrollTop !== showScrollTopButtonRef.current) {
          setShowScrollTopButton(shouldShowScrollTop)
        }
      }
    },
    [],
  )

  const onScroll = useMemo(
    () =>
      Animated.event(
        [{ nativeEvent: { contentOffset: { y: scrollOffsetY } } }],
        {
          useNativeDriver: false,
          listener: scrollListener,
        },
      ),
    [scrollOffsetY, scrollListener],
  )

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: safeAreaBackground }}>
      <View flex={1} backgroundColor="$bg-page" onLayout={handleLayout}>
        <AdminPreviewBanner />
        <IOScrollView
          ref={scrollViewRef}
          scrollEventThrottle={SCROLL_EVENT_THROTTLE}
          bounces={false}
          style={{
            backgroundColor,
            width: '100%',
          }}
          contentContainerStyle={{ flexGrow: 1 }}
          // `isScrollingContent` existe para o scroll aninhado do nativo
          // (HET-520). No web, `scrollEnabled={false}` faz o react-native-web
          // aplicar `overflow: hidden` + `touch-action: none` neste container:
          // a página inteira perde a barra de rolagem. Quem rola aqui é o
          // browser, que já encadeia o scroll aninhado sozinho — HET-4038.
          scrollEnabled={isWeb || !isScrollingContent}
          onContentSizeChange={handleContentSizeChange}
          onScroll={onScroll}
        >
          {!isHighlightedImage && (
            <Header
              backRoute={headerBackRoute}
              logoUrl={logoUrl}
              isFixedHeader
              isVisible={isVisible}
            />
          )}
          <YStack
            backgroundColor={backgroundColor}
            paddingBottom={hasPaddingBottom ? '$size.spacing-lg' : 0}
            flexGrow={1}
            $gtMd={{
              paddingBottom: 0,
            }}
            f={1}
          >
            {children}
          </YStack>
          {hasFooter && <Footer />}
        </IOScrollView>
        {!media.gtMd && hasScrollTopButton && (
          <Animated.View
            pointerEvents={showScrollTopButton ? 'auto' : 'none'}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 110,
              zIndex: 99,
              alignItems: 'center',
              opacity: scrollTopButtonAnim,
              transform: [{ translateY: scrollTopButtonTranslateY }],
            }}
          >
            <TouchableOpacity
              onPress={handleScrollToTop}
              activeOpacity={0.8}
              accessibilityLabel="Voltar ao topo"
            >
              <View
                width={44}
                height={44}
                br={22}
                backgroundColor="$bg-surface"
                borderWidth={1}
                borderColor="$border-strong"
                alignItems="center"
                justifyContent="center"
              >
                <HeroIcon
                  icon={['fal', 'arrow-up']}
                  color="$accent"
                  size={20}
                />
              </View>
            </TouchableOpacity>
          </Animated.View>
        )}
        <Animated.View
          style={{
            zIndex: 100,
            height: isWeb ? animatedHeaderHeight : HEADER_SECTION_HEIGHT,
            position: 'absolute',
            backgroundColor: '$neutral-0',
            marginHorizontal: 'auto',
            width: '100%',
            opacity: isWeb ? animatedOpacity : 1,
            bottom: navigationBottom,
            display: isNavBarVisible ? 'flex' : 'none',
          }}
        >
          {media.gtMd && (
            <Header
              backRoute={headerBackRoute}
              logoUrl={logoUrl}
              isFixedHeader={false}
              isVisible={isVisible}
            />
          )}
          {!media.gtMd && (
            <>
              <BottomNavBar openModal={handleOpenMenu} />
              <MobileMenu open={isMenuOpen} onClose={handleCloseMenu} />
            </>
          )}
        </Animated.View>
      </View>
    </SafeAreaView>
  )
}
